import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Trash2, User, ShieldCheck, Monitor, Briefcase, Edit } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useBodyLock } from '../../hooks/useBodyLock';

interface Administrador {
  id: string | number;
  username?: string;
  nome?: string;
  sobrenome?: string;
  email?: string;
  role?: string;
  nivel?: string;
  created_at: string;
}

interface AdministradorForm {
  username: string;
  password: string;
  role: string;
  nome?: string;
  sobrenome?: string;
  email?: string;
}

const ROLES: Record<string, { label: string; desc: string; color: string; icon: any }> = {
  gestao: { 
    label: 'Gestão', 
    desc: 'Acesso total ao painel e configurações', 
    color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
    icon: ShieldCheck 
  },
  admin: { 
    label: 'Administrador', 
    desc: 'Acesso ao painel administrativo', 
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    icon: Monitor 
  },
  gerente: { 
    label: 'Gerente', 
    desc: 'Acesso de gerência', 
    color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    icon: ShieldCheck 
  },
  colaborador: { 
    label: 'Colaborador', 
    desc: 'Acesso ao painel administrativo', 
    color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
    icon: User 
  },
  caixa: { 
    label: 'Caixa', 
    desc: 'Acesso apenas ao sistema de caixa (PDV)', 
    color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    icon: Briefcase 
  },
  usuario: { 
    label: 'Usuário', 
    desc: 'Acesso padrão', 
    color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300',
    icon: User 
  },
};

const getRoleConfig = (role: string) => ROLES[role] || ROLES['usuario'];

const AdministradoresPage: React.FC = () => {
  const [lista, setLista] = useState<Administrador[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<Administrador | null>(null);

  // Bloquear scroll do body quando modal aberto no mobile
  useBodyLock(showModal);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AdministradorForm>();

  useEffect(() => {
    fetchLista();
  }, []);

  const fetchLista = async () => {
    try {
      const response = await api.get('/api/users');
      const data = Array.isArray(response.data) ? response.data : [];
      setLista(data);
    } catch (error) {
      toast.error('Erro ao carregar administradores e usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (admin: Administrador) => {
    setEditingAdmin(admin);
    reset({
      username: admin.username || '',
      email: admin.email || '',
      nome: admin.nome || '',
      sobrenome: admin.sobrenome || '',
      role: admin.role || admin.nivel || '',
      password: '' // Optional password
    });
    setShowModal(true);
  };

  const onSubmit = async (data: AdministradorForm) => {
    try {
      if (editingAdmin) {
        // Edit mode
        await api.put(`/api/users/${editingAdmin.id}`, data);
        toast.success('Administrador atualizado com sucesso!');
      } else {
        // Create mode
        await api.post('/api/auth/register', {
          username: data.username,
          password: data.password,
          role: data.role,
          nome: data.nome,
          sobrenome: data.sobrenome,
          email: data.email
        });
        toast.success('Administrador cadastrado com sucesso!');
      }
      setShowModal(false);
      setEditingAdmin(null);
      reset();
      fetchLista();
    } catch (error: any) {
      const msg = error.response?.data?.error || error.response?.data?.message || (editingAdmin ? 'Erro ao atualizar' : 'Erro ao cadastrar');
      toast.error(msg);
    }
  };

  const handleDelete = async (id: string | number) => {
    if (!window.confirm('Tem certeza que deseja remover este usuário do acesso?')) return;
    try {
      await api.delete(`/api/users/${id}`);
      toast.success('Acesso removido com sucesso.');
      fetchLista();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao remover');
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const displayName = (item: Administrador) =>
    item.nome && item.sobrenome
      ? `${item.nome} ${item.sobrenome}`.trim()
      : item.username || item.email || `ID ${item.id}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Administradores e Acesso
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Cadastre novos administradores e gerencie clientes com acesso ao caixa e ao painel administrativo.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditingAdmin(null);
            reset();
            setShowModal(true);
          }}
          className="btn-primary flex items-center gap-2 shrink-0"
        >
          <Plus className="h-4 w-4" />
          Novo Administrador
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flow-root">
            <ul className="-my-5 divide-y divide-gray-200 dark:divide-gray-700">
              {lista.length === 0 ? (
                <li className="py-8 text-center text-gray-500 dark:text-gray-400">
                  Nenhum usuário com acesso cadastrado. Clique em &quot;Novo Administrador&quot; para começar.
                </li>
              ) : (
                lista.map((item) => (
                  <li key={String(item.id)} className="py-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-indigo-500 flex items-center justify-center">
                          <User className="h-5 w-5 text-white" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {displayName(item)}
                        </p>
                        {(item.email || item.username) && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                            {item.email || item.username}
                          </p>
                        )}
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          {(() => {
                            const roleKey = (item.role || item.nivel || 'usuario') as string;
                            const config = getRoleConfig(roleKey);
                            const Icon = config.icon;
                            
                            return (
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
                                <Icon className="h-3 w-3" />
                                {config.label}
                              </span>
                            );
                          })()}
                        </div>
                        {item.created_at && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Cadastro: {formatDate(item.created_at)}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={() => handleEdit(item)}
                          className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item.id)}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Remover acesso"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full shadow-xl">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {editingAdmin ? 'Editar Administrador' : 'Novo Administrador'}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {editingAdmin ? 'Atualize os dados do administrador.' : 'Defina o login e o tipo de acesso (caixa e/ou painel administrativo).'}
              </p>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Nome
                    </label>
                    <input
                      {...register('nome')}
                      type="text"
                      className="input-field"
                      placeholder="Nome"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Sobrenome
                    </label>
                    <input
                      {...register('sobrenome')}
                      type="text"
                      className="input-field"
                      placeholder="Sobrenome"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email
                  </label>
                  <input
                    {...register('email', { 
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Email inválido"
                      }
                    })}
                    type="email"
                    className="input-field"
                    placeholder="email@exemplo.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nome de usuário (login)
                  </label>
                  <input
                    {...register('username', { required: 'Nome de usuário é obrigatório' })}
                    type="text"
                    className="input-field"
                    placeholder="Ex: joao.silva"
                  />
                  {errors.username && (
                    <p className="mt-1 text-sm text-red-600">{errors.username.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Senha {editingAdmin && <span className="text-xs text-gray-500 font-normal">(Deixe em branco para manter a atual)</span>}
                  </label>
                  <input
                    {...register('password', {
                      required: editingAdmin ? false : 'Senha é obrigatória',
                      minLength: { value: 6, message: 'Mínimo 6 caracteres' },
                    })}
                    type="password"
                    className="input-field"
                    placeholder={editingAdmin ? "Nova senha (opcional)" : "••••••••"}
                  />
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Perfil de acesso
                  </label>
                  <select
                    {...register('role', { required: 'Selecione o perfil de acesso' })}
                    className="input-field"
                  >
                    <option value="">Selecione o perfil</option>
                    <option value="gestao">Gestão (painel + configurações)</option>
                    <option value="colaborador">Colaborador (painel administrativo)</option>
                    <option value="caixa">Acesso Caixa (apenas PDV)</option>
                    <option value="admin">Acesso Painel ADM</option>
                  </select>
                  {errors.role && (
                    <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 btn-primary">
                    {editingAdmin ? 'Salvar Alterações' : 'Cadastrar'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingAdmin(null);
                      reset();
                    }}
                    className="flex-1 btn-secondary"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdministradoresPage;
